import { z } from 'zod';
import { roleSchema } from '../roles.js';
import { userDetailSchema } from './users.js';
/**
 * Bulk import of accounts (REQUIREMENTS §2.7).
 *
 * The spreadsheet is parsed in the browser; what reaches the server is rows
 * already mapped to these fields. Units are named the way a spreadsheet names
 * them — by platoon and team *name* — and resolved to ids on the server.
 *
 * Two endpoints with the same body. `validate` writes nothing and returns a
 * per-row report; `commit` re-runs exactly the same validation and refuses the
 * whole batch on any error. There is no partial commit, silent or otherwise
 * (§2.12): a file with one bad row imports nobody until the row is fixed.
 */
export const importRowSchema = z.object({
    email: z.string().trim().max(254),
    personalNumber: z.string().trim().max(20),
    firstName: z.string().trim().max(80),
    lastName: z.string().trim().max(80),
    rank: z.string().trim().max(40).nullable().default(null),
    title: z.string().trim().max(80).nullable().default(null),
    phone: z.string().trim().max(40).nullable().default(null),
    platoon: z.string().trim().max(80),
    team: z.string().trim().max(80),
    /** Comma-separated in a spreadsheet; split by the client. Defaults to soldier. */
    roles: z.array(z.string().trim()).max(5).default([]),
});
/** A whole company fits comfortably; anything larger is a different tool. */
export const MAX_IMPORT_ROWS = 500;
export const importRequestSchema = z.object({
    rows: z.array(importRowSchema).min(1).max(MAX_IMPORT_ROWS),
});
/**
 * Why a row was refused. Codes, not sentences — the client translates them,
 * and a spreadsheet of Hebrew names deserves a Hebrew error column.
 */
export const importIssueCodeSchema = z.enum([
    'email_invalid',
    'email_duplicate_in_file',
    'email_taken',
    'personal_number_invalid',
    'personal_number_duplicate_in_file',
    'personal_number_taken',
    'first_name_missing',
    'last_name_missing',
    'platoon_unknown',
    'platoon_inactive',
    'team_unknown',
    'team_inactive',
    'role_unknown',
]);
export const importIssueSchema = z.object({
    field: z.enum([
        'email',
        'personalNumber',
        'firstName',
        'lastName',
        'platoon',
        'team',
        'roles',
    ]),
    code: importIssueCodeSchema,
});
export const importRowReportSchema = z.object({
    /** 0-based index into the submitted rows. */
    index: z.number().int().min(0),
    issues: z.array(importIssueSchema),
    /** Resolved on a clean row so the preview can show the real unit; null with a platoon when the team column is empty (story 32). */
    teamId: z.string().nullable(),
    platoonId: z.string().nullable().default(null),
    roles: z.array(roleSchema),
});
export const importValidateResponseSchema = z.object({
    rows: z.array(importRowReportSchema),
    valid: z.number().int().min(0),
    invalid: z.number().int().min(0),
});
/**
 * The commit's answer. `created` is every account that now exists — on
 * success, all of them; on a mid-batch failure, none, because the server
 * rolled back what it had made and `rolledBack` says so.
 */
export const importCommitResponseSchema = z.object({
    created: z.array(userDetailSchema),
    rolledBack: z.boolean(),
    /** Present only when the batch was refused before anything was written. */
    report: importValidateResponseSchema.nullable(),
});
//# sourceMappingURL=import.js.map